import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useOrganization } from '@context/OrganizationContext';
import { Loading } from '@goodfynd/ui';
import { useConfirmationDialog } from '@goodfynd/ui';
import useOrganizationBusinessInfo from '@hooks/useOrganizationBusinessInfo';
import useOrganizationCreateOnboardingLink from '@hooks/useOrganizationCreateOnboardingLink';
import type { CreateOnboardingLinkResponse } from '@services/api/types';
import { useRouter } from 'next/router';
import * as uuid from 'uuid';

import { useStores } from '../../../stores';

import OrderingAlert from './OrderingAlert';
import type { AlertEnableOrderingProps } from './types';

import classes from './OrderingStatusAlert.module.css';

export default function OnboardStripe({
  compact,
  css,
  ctaText = 'Continue Account Onboarding',
  text = "You're almost there, just a few more steps!",
}: AlertEnableOrderingProps) {
  const token = useMemo(() => uuid.v4(), []);
  const { getConfirmation } = useConfirmationDialog();

  const router = useRouter();
  const { query } = router;

  const {
    orderStore: { setToken },
  } = useStores();

  const { organizationId } = useOrganization();
  const vendorNameId = query.vendorId as string | undefined;

  const { businessInfo, isLoading } = useOrganizationBusinessInfo({
    organizationId,
  });

  useEffect(() => {
    if (
      router.isReady &&
      !query.state &&
      !isLoading &&
      !businessInfo?.orderingEnabled
    ) {
      setToken?.(token);
    }
  }, [
    router.isReady,
    query.state,
    isLoading,
    businessInfo?.orderingEnabled,
    businessInfo?.id,
    setToken,
    token,
  ]);

  // Gate redirect so the hook's auto-run (enabled: !!organizationId) doesn't bounce the user on mount.
  const shouldRedirectRef = useRef(false);

  const onSuccess = useCallback((data: { onboardingLink?: string | null }) => {
    if (!shouldRedirectRef.current) return;
    shouldRedirectRef.current = false;

    const link = data?.onboardingLink;
    if (link) window.location.href = link;
  }, []);

  const {
    createLink: createOnboardingLink,
    isFetching: isFetchingOnboardingLink,
  } = useOrganizationCreateOnboardingLink({
    onSuccess: onSuccess as
      | ((data: CreateOnboardingLinkResponse) => void)
      | undefined,
    organizationId,
    vendorNameId,
  });

  const busy = isFetchingOnboardingLink;

  const onboardStripe = useCallback(async () => {
    const confirmed = await getConfirmation({
      title: ctaText,
      message:
        'We use Stripe to make sure you get paid on time and to keep all of your personal information secure.',
      confirmButtonText: 'Continue Stripe Onboarding',
    });

    if (!confirmed) return;

    shouldRedirectRef.current = true;
    await createOnboardingLink();
  }, [getConfirmation, ctaText, createOnboardingLink]);

  return busy ? (
    <div className={classes.overlay}>
      <Loading />
    </div>
  ) : (
    <OrderingAlert
      buttonClick={onboardStripe}
      compact={compact}
      css={css}
      ctaText={ctaText}
      icon="info"
      text={text}
      type="warning"
    />
  );
}
